import * as Api from '@ViewModels';

export interface ICompanyTagFilterLists {
	orTags: string[];
	andTags: string[];
	notTags: string[];
}

/** Case-insensitive, whitespace-trimmed comparison so the same tag isn't added twice. */
export const matchesTag = (a: string, b: string) => a.trim().toLocaleLowerCase() === b.trim().toLocaleLowerCase();

/** Append a tag to a list, ignoring blanks and case-insensitive duplicates. Returns the same list when nothing changes. */
export const addTag = (collection: string[], tag: string) => {
	if (!tag?.trim() || collection.some(x => matchesTag(x, tag))) {
		return collection;
	}
	return [...collection, tag];
};

/** Remove a tag from a list (case-insensitive match). */
export const removeTag = (collection: string[], tag: string) => collection.filter(x => !matchesTag(x, tag));

export const hasCompanyTagFilters = ({ orTags, andTags, notTags }: ICompanyTagFilterLists) => {
	return orTags.length > 0 || andTags.length > 0 || notTags.length > 0;
};

const toTagLeaf = (tag: string): Api.CompanyFilterCriteria => ({
	property: Api.CompanyFilterCriteriaProperty.Tag,
	value: tag,
});

/**
 * Builds the recursive criteria payload for POST company/filter/v2 from the three tag lists.
 * Top-level criteria combine with the default "And" op, matching the contacts advanced tag search shape:
 * OR tags and AND tags each become a nested group; each NOT tag becomes a negated top-level leaf.
 *
 * An optional `namePrefix` adds a case-insensitive starts-with match on the company name (the
 * `NamePrefix` property), AND-ed with the tag criteria. Tag filters are still required: name alone
 * returns undefined so a plain name search stays on the default v1 endpoint.
 */
export const buildCompanyTagFilter = (
	lists: ICompanyTagFilterLists,
	namePrefix?: string
): Api.CompanyFilter | undefined => {
	const { orTags, andTags, notTags } = lists;
	if (!hasCompanyTagFilters(lists)) {
		return undefined;
	}

	const criteria: Api.CompanyFilterCriteria[] = [];

	const trimmedNamePrefix = namePrefix?.trim();
	if (trimmedNamePrefix) {
		criteria.push({ property: Api.CompanyFilterCriteriaProperty.NamePrefix, value: trimmedNamePrefix });
	}

	if (orTags.length === 1 && !andTags.length) {
		criteria.push(toTagLeaf(orTags[0]));
	} else if (orTags.length > 0) {
		criteria.push({
			property: Api.CompanyFilterCriteriaProperty.Nested,
			op: Api.FilterOperator.Or,
			criteria: orTags.map(toTagLeaf),
		});
	}

	if (andTags.length === 1 && !orTags.length) {
		criteria.push(toTagLeaf(andTags[0]));
	} else if (andTags.length > 0) {
		criteria.push({
			property: Api.CompanyFilterCriteriaProperty.Nested,
			op: Api.FilterOperator.And,
			criteria: andTags.map(toTagLeaf),
		});
	}

	notTags.forEach(tag => {
		criteria.push({
			...toTagLeaf(tag),
			op: Api.FilterOperator.Not,
		});
	});

	return { criteria };
};
